import { useCallback, useMemo } from 'react';

import { useQueryClient } from 'react-query';

import { appendSeenGuide } from 'api/rest/users/seenGuides';
import usersApi from 'api/rest/users/users';
import type { Me } from 'api/rest/users/users.types';
import QUERY from 'constants/queryKeys';
import useMeQuery from 'hooks/useMeQuery/useMeQuery';

import { applyShowTutorialMigration } from './guideIds';

const useSeenGuides = () => {
  const queryClient = useQueryClient();
  const { data: me, isSuccess } = useMeQuery();
  const seenGuides = useMemo(
    () => applyShowTutorialMigration(me?.seenGuides ?? [], me?.showTutorial),
    [me?.seenGuides, me?.showTutorial],
  );

  const markSeen = useCallback(
    async (guideId: string) => {
      const current = queryClient.getQueryData<Me | null>(QUERY.GET_ME);

      if (!current) {
        return;
      }

      const currentGuides = current.seenGuides ?? [];
      const next = appendSeenGuide(currentGuides, guideId);

      if (next.length === currentGuides.length && next.every((id, index) => id === currentGuides[index])) {
        return;
      }

      queryClient.setQueryData<Me | null>(QUERY.GET_ME, { ...current, seenGuides: next });

      try {
        await usersApi.markSeenGuides(next);
      } catch {
        queryClient.setQueryData<Me | null>(QUERY.GET_ME, current);
      }
    },
    [queryClient],
  );

  return {
    seenGuides,
    isReady: isSuccess,
    markSeen,
  };
};

export default useSeenGuides;
